import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Switch
} from '@mui/material';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchLocations } from '../../store/locationStore/locationThunk';
import {
    Security as SecurityIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import useModulePermissions from '../../hooks/useModulePermissions';
import ModuleAccessDialog from './ModuleAccessDialog';
import permissionService from '../../services/PermissionService';
import { usePermissions } from '../../hooks/usePermissions';

interface User {
    _id: string;
    name: string;
    email: string;
}

interface UserPermissionsViewProps {
    user: User;
    onClose?: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`permission-tabpanel-${index}`}
            aria-labelledby={`permission-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const UserPermissionsView: React.FC<UserPermissionsViewProps> = ({ user, onClose }) => {
    const {
        userPermissions,
        availableModules,
        availableScopes,
        permissionHistory,
        loading,
        saving,
        error,
        loadUserPermissions,
        loadPermissionHistory,
        grantModuleAccess,
        revokeModuleAccess,
        updateContextPermissions,
        setUserLocations,
        setFullAccess,
        clearError
    } = useModulePermissions();

    const dispatch = useAppDispatch();
    const { locations } = useAppSelector((state: any) => state.locations);
    const { user: loggedInUser, modulePermissions } = useAppSelector((state: any) => state.auth);
    const { isAdmin } = usePermissions();

    // ── Admin-level access ───────────────────────────────────────────────────
    // Giving someone every permission the account owner has is the single most
    // powerful action in this dialog, so the control is hidden unless the
    // LOGGED-IN user is the account owner or an Admin. The server enforces the
    // same rule (canGrantFullAccess) — this only decides what is rendered.
    const [canGrantFullAccess, setCanGrantFullAccess] = useState(false);
    const [fullAccessDialogOpen, setFullAccessDialogOpen] = useState(false);
    const [savingFullAccess, setSavingFullAccess] = useState(false);

    const targetHasFullAccess = !!userPermissions?.hasFullAccess;
    const isSelf = String(loggedInUser?._id || '') === String(user._id);

    // Optimistic local gate (owner / Admin) confirmed by the server.
    // hasFullAccess is deliberately NOT part of this: someone who was given
    // admin-level access must not be able to pass it on or strip it from a real
    // Admin. isAdmin() reads the ASSIGNED role, unchanged by the overlay.
    useEffect(() => {
        if (!modulePermissions?.isAccountOwner && !isAdmin()) {
            setCanGrantFullAccess(false);
            return;
        }
        let active = true;
        permissionService.canGrantFullAccess()
            .then((allowed) => { if (active) setCanGrantFullAccess(allowed); })
            .catch(() => { if (active) setCanGrantFullAccess(false); });
        return () => { active = false; };
    }, [modulePermissions, isAdmin]);

    const handleConfirmFullAccess = async () => {
        setSavingFullAccess(true);
        try {
            await setFullAccess(user._id, !targetHasFullAccess);
            toast.success(
                targetHasFullAccess
                    ? 'Admin-level access revoked'
                    : 'Admin-level access granted — the role is unchanged'
            );
            setFullAccessDialogOpen(false);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to update admin-level access');
        } finally {
            setSavingFullAccess(false);
        }
    };

    const [tabValue, setTabValue] = useState(0);
    // Location Access: the locations this user may access (empty = all).
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [savingLocations, setSavingLocations] = useState(false);

    // Load the account's locations once (for the Location Access picker).
    useEffect(() => {
        dispatch(fetchLocations());
    }, [dispatch]);

    // Sync the picker with the user's saved locations whenever they load.
    useEffect(() => {
        setSelectedLocations((userPermissions?.locations as string[]) || []);
    }, [userPermissions]);

    const toggleLocation = (locationId: string, checked: boolean) => {
        setSelectedLocations(prev =>
            checked ? [...prev, locationId] : prev.filter(id => id !== locationId)
        );
    };

    const handleSaveLocations = async () => {
        setSavingLocations(true);
        try {
            await setUserLocations(user._id, selectedLocations);
            toast.success('Location access updated');
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to update location access');
        } finally {
            setSavingLocations(false);
        }
    };
    const [dialogOpen, setDialogOpen] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [moduleToRevoke, setModuleToRevoke] = useState<{ module: string; subModule?: string | null } | null>(null);

    useEffect(() => {
        if (user?._id) {
            loadUserPermissions(user._id);
        }
    }, [user?._id, loadUserPermissions]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        
        // Load history when switching to history tab (now the 4th tab, index 3)
        if (newValue === 3 && user?._id && permissionHistory.length === 0) {
            loadPermissionHistory(user._id);
        }
    };

    const handleSavePermissions = async (data: any) => {
        if (data.type === 'module') {
            await grantModuleAccess(user._id, data.data, data.reason);
        } else if (data.type === 'context') {
            await updateContextPermissions(user._id, data.data, data.reason);
        }
    };

    const handleRevokeModule = (module: string, subModule?: string | null) => {
        setModuleToRevoke({ module, subModule: subModule || null });
        setRevokeDialogOpen(true);
    };

    const confirmRevokeModule = async () => {
        if (moduleToRevoke) {
            try {
                await revokeModuleAccess(user._id, moduleToRevoke.module, 'Manual revocation', moduleToRevoke.subModule);
                setRevokeDialogOpen(false);
                setModuleToRevoke(null);
            } catch (error) {
                // Error is handled by the hook
            }
        }
    };

    const getModuleName = (moduleId: string) => {
        const module = availableModules.find(m => m.id === moduleId);
        return module ? module.name : moduleId;
    };

    const getScopeName = (scopeId: string) => {
        const scope = availableScopes.find(s => s.id === scopeId);
        return scope ? scope.name : scopeId;
    };

    // Label for the user's assigned role, e.g. 'manager' -> 'Manager'.
    const baseRoleLabel = userPermissions?.baseRole
        ? userPermissions.baseRole.charAt(0).toUpperCase() + userPermissions.baseRole.slice(1)
        : 'their role';

    const getSubModuleName = (moduleId: string, subModuleId?: string | null) => {
        if (!subModuleId) return '';
        const module = availableModules.find(m => m.id === moduleId);
        const sub = module?.subModules?.find(s => s.id === subModuleId);
        return sub ? sub.name : subModuleId;
    };

    if (loading && !userPermissions) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h5" gutterBottom>
                    User Permissions - {user.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {user.email}
                    {userPermissions?.baseRole ? ` (${baseRoleLabel})` : ''}
                    {targetHasFullAccess && (
                        <Chip
                            label="Admin-level access"
                            size="small"
                            color="success"
                            sx={{ ml: 1, height: 20, fontSize: 11, fontWeight: 600 }}
                        />
                    )}
                </Typography>
            </Box>

            {/* Admin-level access — visible only to the account owner / an Admin. */}
            {canGrantFullAccess && (
                <Card
                    variant="outlined"
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        boxShadow: 'none',
                        borderColor: targetHasFullAccess ? '#059669' : '#e5e7eb',
                        bgcolor: targetHasFullAccess ? '#ecfdf5' : 'transparent',
                    }}
                >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={2}
                            flexWrap="wrap"
                        >
                            <Box sx={{ minWidth: 260, flex: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <SecurityIcon fontSize="small" sx={{ color: '#047857' }} />
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Admin-Level Access
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    {targetHasFullAccess
                                        ? `This user currently has every permission an Admin has. Their role is still ${baseRoleLabel}.`
                                        : `Give this user every permission an Admin has. Their role stays ${baseRoleLabel} — only their access changes.`}
                                </Typography>
                            </Box>
                            {/* Toggle, not a button — the state IS "on or off", and the
                                confirmation dialog is what actually commits the change,
                                so the switch stays where the server has it until then. */}
                            <Tooltip
                                title={isSelf ? 'You cannot change your own admin-level access.' : ''}
                            >
                                <span>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={targetHasFullAccess}
                                                onChange={() => setFullAccessDialogOpen(true)}
                                                disabled={savingFullAccess || isSelf}
                                                color="primary"
                                            />
                                        }
                                        label={targetHasFullAccess ? 'Enabled' : 'Give All Permissions'}
                                        labelPlacement="start"
                                        sx={{ mr: 0, ml: 0, color: '#5a6472' }}
                                    />
                                </span>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Location Access" icon={<BusinessIcon />} />
                    <Tab label="Module Access" icon={<SecurityIcon />} />
                    <Tab label="Manager Permissions" icon={<PersonIcon />} />
                    <Tab label="History" icon={<HistoryIcon />} />
                </Tabs>
            </Box>

            {/* Location Access (first tab) */}
            <TabPanel value={tabValue} index={0}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">Location Access</Typography>
                        <Button
                            variant="contained"
                            onClick={handleSaveLocations}
                            disabled={savingLocations || targetHasFullAccess}
                        >
                            {savingLocations ? 'Saving...' : 'Save Locations'}
                        </Button>
                    </Box>
                    {targetHasFullAccess ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Admin-level access covers <strong>all locations</strong>, so location restrictions do not
                            apply to this user right now.
                        </Alert>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Select the locations this user can access. <strong>No selection = all locations</strong> (no restriction).
                            Location access applies on top of Module Access — a user still needs module permissions.
                        </Alert>
                    )}

                    {(!locations || locations.length === 0) ? (
                        <Typography variant="body2" color="textSecondary">No locations found for this account.</Typography>
                    ) : (
                        <FormGroup>
                            {locations.map((loc: any) => (
                                <FormControlLabel
                                    key={loc._id}
                                    control={
                                        <Checkbox
                                            checked={selectedLocations.includes(loc._id)}
                                            onChange={(e) => toggleLocation(loc._id, e.target.checked)}
                                        />
                                    }
                                    label={`${loc.name}${loc.code ? ` (${loc.code})` : ''}`}
                                />
                            ))}
                        </FormGroup>
                    )}
                    {selectedLocations.length === 0 && locations && locations.length > 0 && (
                        <Typography variant="caption" color="textSecondary">
                            Currently: all locations (no restriction).
                        </Typography>
                    )}
                </Box>
            </TabPanel>

            {/* Module Access */}
            <TabPanel value={tabValue} index={1}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Module Access Permissions</Typography>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setDialogOpen(true)}
                            disabled={saving || targetHasFullAccess}
                        >
                            Manage Access
                        </Button>
                    </Box>

                    {targetHasFullAccess ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            This user has <strong>admin-level access</strong> — every module and sub-module below is
                            granted in full. Individual grants are disabled while it is on; revoke it above to go
                            back to their {baseRoleLabel} role and per-module grants.
                        </Alert>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Modules marked <strong>From {baseRoleLabel} role</strong> are granted automatically by the
                            user's role and cannot be revoked here — change their role instead. Grants added below only
                            ever <strong>widen</strong> that default.
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        {userPermissions && Object.keys(userPermissions.moduleAccess).length > 0 ? (
                            Object.entries(userPermissions.moduleAccess).map(([module, access]: [string, any]) => (
                                <Grid item xs={12} sm={6} md={4} key={module}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            borderRadius: 2,
                                            borderColor: '#e5e7eb',
                                            transition: 'box-shadow .2s ease, border-color .2s ease, transform .2s ease',
                                            '&:hover': {
                                                boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                                                borderColor: '#059669',
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={700}
                                                        noWrap
                                                        title={getModuleName(module)}
                                                    >
                                                        {getModuleName(module)}
                                                    </Typography>
                                                    {access.source && access.source !== 'user' && (
                                                        <Chip
                                                            label={
                                                                access.source === 'full-access'
                                                                    ? 'Admin-level access'
                                                                    : access.source === 'role+user'
                                                                        ? `${baseRoleLabel} role + extra`
                                                                        : `From ${baseRoleLabel} role`
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                                                        />
                                                    )}
                                                </Box>
                                                {access.source === 'role' || access.source === 'full-access' ? (
                                                    <Tooltip
                                                        title={
                                                            access.source === 'full-access'
                                                                ? 'Comes from admin-level access. Revoke that above to remove it.'
                                                                : `Comes from the ${baseRoleLabel} role. Change the user's role to remove it.`
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton size="small" disabled sx={{ flexShrink: 0 }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        title="Revoke entire module"
                                                        onClick={() => handleRevokeModule(module)}
                                                        disabled={saving}
                                                        sx={{ flexShrink: 0 }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {/* Module-level permissions (grant with no sub-module) */}
                                            {access.permissions && access.permissions.length > 0 && (
                                                <>
                                                    <Divider sx={{ mt: 1, mb: 1.25 }} />
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        mb={0.75}
                                                        gap={1}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                                        >
                                                            Permissions
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                px: 1,
                                                                py: 0.25,
                                                                borderRadius: 999,
                                                                bgcolor: '#ecfdf5',
                                                                color: '#047857',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <Typography variant="caption" fontWeight={600} lineHeight={1.6}>
                                                                {getScopeName(access.scope)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {access.permissions.map((permission: string) => (
                                                            <Chip
                                                                key={permission}
                                                                label={permission}
                                                                size="small"
                                                                sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                </>
                                            )}

                                            {/* Per sub-module grants, each independently revocable */}
                                            {access.subModules && Object.keys(access.subModules).length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    {Object.entries(access.subModules).map(([subId, subAccess]: [string, any]) => (
                                                        <Box key={subId} sx={{ mt: 1, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {getSubModuleName(module, subId)}
                                                                    <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                                                                        ({getScopeName(subAccess.scope)})
                                                                    </Typography>
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    title={
                                                                        access.source === 'full-access'
                                                                            ? 'Comes from admin-level access'
                                                                            : 'Revoke sub-module'
                                                                    }
                                                                    onClick={() => handleRevokeModule(module, subId)}
                                                                    disabled={saving || access.source === 'full-access'}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                            <Box sx={{ mt: 0.5 }}>
                                                                {subAccess.permissions.map((permission: string) => (
                                                                    <Chip
                                                                        key={permission}
                                                                        label={permission}
                                                                        size="small"
                                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                                        variant="outlined"
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    No module access permissions assigned. Click "Manage Access" to add permissions.
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Manager & Approval Permissions</Typography>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setDialogOpen(true)}
                            disabled={saving || targetHasFullAccess}
                        >
                            Update Permissions
                        </Button>
                    </Box>

                    {targetHasFullAccess ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Admin-level access grants <strong>every</strong> manager and approval right below.
                        </Alert>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            These start from what the <strong>{baseRoleLabel}</strong> role grants. Updating them here can
                            only add rights on top of that baseline.
                        </Alert>
                    )}

                    {userPermissions?.contextPermissions ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ pl: 2 }}>
                                            General Manager Status
                                        </Typography>
                                        <List dense sx={{ '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                                            {/* Hidden for now — see ModuleAccessDialog.
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.isManager ?
                                                        <CheckIcon color="success" /> :
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Is Manager" />
                                            </ListItem>
                                            */}
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canAssignRoles ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Assign Roles" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ pl: 2 }}>
                                            Approval Permissions
                                        </Typography>
                                        <List dense sx={{ '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveLeaves ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Leaves" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveAttendance ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Attendance" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveTimesheets ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Timesheets" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {userPermissions.contextPermissions.canApproveExpenses ? 
                                                        <CheckIcon color="success" /> : 
                                                        <CloseIcon color="error" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary="Can Approve Expenses" />
                                            </ListItem>
                                        </List>

                                        {userPermissions.contextPermissions.approvalScope && (
                                            <Box sx={{ mt: 1, pl: 2 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Approval Scope: <strong>{userPermissions.contextPermissions.approvalScope}</strong>
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Alert severity="info">
                            No manager permissions assigned.
                        </Alert>
                    )}
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
                <Typography variant="h6" gutterBottom>
                    Permission Change History
                </Typography>

                {permissionHistory.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Module</TableCell>
                                    <TableCell>Performed By</TableCell>
                                    <TableCell>Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {permissionHistory.map((entry) => (
                                    <TableRow key={entry._id}>
                                        <TableCell>
                                            {format(new Date(entry.performedAt), 'MMM dd, yyyy HH:mm')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={entry.action}
                                                size="small"
                                                color={
                                                    entry.action === 'granted' ? 'success' :
                                                    entry.action === 'revoked' ? 'error' :
                                                    'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{entry.changeType}</TableCell>
                                        <TableCell>{entry.module || '-'}</TableCell>
                                        <TableCell>{entry.performedBy.name}</TableCell>
                                        <TableCell>
                                            <Tooltip title={entry.reason || 'No reason provided'}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                    {entry.reason || '-'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info">
                        No permission changes recorded.
                    </Alert>
                )}
            </TabPanel>

            {/* Module Access Dialog */}
            <ModuleAccessDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                selectedUser={user}
                onSave={handleSavePermissions}
                availableModules={availableModules}
                availableScopes={availableScopes}
                existingPermissions={userPermissions}
            />

            {/* Admin-Level Access Confirmation */}
            <Dialog open={fullAccessDialogOpen} onClose={() => setFullAccessDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {targetHasFullAccess ? 'Revoke Admin-Level Access' : 'Give All Permissions'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {targetHasFullAccess ? (
                            <>
                                Remove admin-level access from <strong>{user.name}</strong>? They will fall back
                                to what their {baseRoleLabel} role and their individual grants allow.
                            </>
                        ) : (
                            <>
                                Give <strong>{user.name}</strong> every permission an Admin has — all modules,
                                all locations and all approval rights.
                            </>
                        )}
                    </Typography>
                    <Alert severity={targetHasFullAccess ? 'warning' : 'info'} sx={{ mt: 2 }}>
                        Their role stays <strong>{baseRoleLabel}</strong>. This changes access only, and can be
                        reversed here at any time.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFullAccessDialogOpen(false)} disabled={savingFullAccess}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmFullAccess}
                        color={targetHasFullAccess ? 'error' : 'primary'}
                        variant="contained"
                        disabled={savingFullAccess}
                    >
                        {savingFullAccess
                            ? 'Saving...'
                            : targetHasFullAccess ? 'Revoke Access' : 'Give All Permissions'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirmation Dialog */}
            <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
                <DialogTitle>Revoke Module Access</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to revoke access to{' '}
                        <strong>
                            {moduleToRevoke ? getModuleName(moduleToRevoke.module) : ''}
                            {moduleToRevoke?.subModule ? ` › ${getSubModuleName(moduleToRevoke.module, moduleToRevoke.subModule)}` : ''}
                        </strong>
                        {moduleToRevoke?.subModule ? '?' : ' (whole module)?'}
                        {' '}This action cannot be undone.
                    </Typography>
                    {moduleToRevoke && userPermissions?.moduleAccess[moduleToRevoke.module]?.source === 'role+user' && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This removes only the extra access granted to this user. The permissions their
                            {' '}{baseRoleLabel} role provides will remain.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRevokeModule}
                        color="error"
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? 'Revoking...' : 'Revoke Access'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserPermissionsView;